// src/context/AuthProvider.tsx
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Identity } from "@dfinity/agent";
import { Principal } from "@dfinity/principal";
import { ActorSubclass } from "@dfinity/agent";
import { _SERVICE } from "../../declarations/cnstudio_backend/cnstudio_backend.did";
import { USER_SERVICE } from "./components/UserCanister/candid/main.did";
import { AuthClient } from "@dfinity/auth-client";
import { createActor } from "../../declarations/cnstudio_backend";
import { createUserActor } from "./components/UserCanister/candid/index";

// Interface for canister info
interface CanisterInfo {
    principal: Principal;
    name: string;
    canisterType: string;
}

// Interface for system canisters
interface SystemCanisters {
    platformPrincipal: Principal | null;
    platformActor: ActorSubclass<_SERVICE> | null;
    userPrincipal: Principal | null;
    userActor: ActorSubclass<USER_SERVICE> | null;
}

// Interface for the auth context
interface AuthContextType {
    isLoggedIn: boolean;
    setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
    userPrincipal: Principal | null;
    setUserPrincipal: React.Dispatch<React.SetStateAction<Principal | null>>;
    identity: Identity | null;
    setIdentity: React.Dispatch<React.SetStateAction<Identity | null>>;
    systemCanisters: SystemCanisters;
    setSystemCanisters: React.Dispatch<React.SetStateAction<SystemCanisters>>;
    error: string | null;
    setError: React.Dispatch<React.SetStateAction<string | null>>;
    activeCanister: CanisterInfo | null;
    setActiveCanister: React.Dispatch<React.SetStateAction<CanisterInfo | null>>;
    isAuthReady: boolean;
    restoreAuth: () => Promise<boolean>;
    userPlatformCanister: Principal | null;
    setUserPlatformCanister: React.Dispatch<React.SetStateAction<Principal | null>>;
    isAdmin: boolean;
    setIsAdmin: React.Dispatch<React.SetStateAction<boolean>>;
    checkAdminStatus: () => Promise<boolean>;
    selectedProjectId: string;
    setSelectedProjectId: React.Dispatch<React.SetStateAction<string>>;
}

// Create the context with initial null value
const AuthContext = createContext<AuthContextType | null>(null);

// Props for the provider component
interface AuthProviderProps {
    children: ReactNode;
}

// Context provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
    const [userPrincipal, setUserPrincipal] = useState<Principal | null>(null);
    const [identity, setIdentity] = useState<Identity | null>(null);
    const [systemCanisters, setSystemCanisters] = useState<SystemCanisters>({
        platformPrincipal: null,
        platformActor: null,
        userPrincipal: null,
        userActor: null,
    });
    const [error, setError] = useState<string | null>(null);
    const [activeCanister, setActiveCanister] = useState<CanisterInfo | null>(null);
    const [isAuthReady, setIsAuthReady] = useState<boolean>(false);
    const [userPlatformCanister, setUserPlatformCanister] = useState<Principal | null>(null);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [selectedProjectId, setSelectedProjectId] = useState<string>('none');

    // Helper function to create authenticated actors
    const createAuthenticatedActor = (canisterId: string, identity: Identity) => {
        const host = process.env.DFX_NETWORK === "ic"
            ? "https://icp0.io"
            : "http://localhost:4943";

        return createActor(canisterId, {
            agentOptions: {
                host,
                identity,
                fetchRootKey: process.env.DFX_NETWORK !== "ic",
                callOptions: {
                    maxTimeToLive: BigInt(5 * 60_000_000_000)
                }
            } as any
        });
    };

    const createAuthenticatedUserActor = (canisterId: string, identity: Identity) => {
        const host = process.env.DFX_NETWORK === "ic"
            ? "https://icp0.io"
            : "http://localhost:4943";

        return createUserActor(canisterId, {
            agentOptions: {
                host,
                identity,
                fetchRootKey: process.env.DFX_NETWORK !== "ic",
                callOptions: {
                    maxTimeToLive: BigInt(5 * 60_000_000_000)
                }
            } as any
        });
    };

    // Get platform canister ID
    const getPlatformCanisterId = async (): Promise<string> => {
        try {
            if (process.env.CANISTER_ID_CNSTUDIO_BACKEND) {
                return process.env.CANISTER_ID_CNSTUDIO_BACKEND;
            }

            const response = await fetch('/canister_ids.json');
            if (!response.ok) {
                throw new Error('Failed to fetch canister IDs');
            }

            const canisterIds = await response.json();
            const networkKey = process.env.DFX_NETWORK || 'local';

            if (canisterIds?.cnstudio_backend?.[networkKey]) {
                return canisterIds.cnstudio_backend[networkKey];
            }

            throw new Error('Platform canister ID not found');
        } catch (error) {
            console.error('Error getting platform canister ID:', error);
            throw error;
        }
    };

    const checkAdminStatus = async (): Promise<boolean> => {
        console.log("checkAdminStatus called, platformActor exists:", !!systemCanisters.platformActor);

        try {
            if (!systemCanisters.platformActor) {
                console.log("Platform actor not initialized, can't check admin status");
                return false;
            }

            console.log("Calling platformActor.isAdmin()...");
            const adminStatus = await systemCanisters.platformActor.isAdmin();
            console.log("Admin status check result:", adminStatus);
            setIsAdmin(adminStatus);
            return adminStatus;
        } catch (err) {
            console.error('Error checking admin status:', err);
            console.error('Error details:', err instanceof Error ? err.message : String(err));
            setIsAdmin(false);
            return false;
        }
    };

    // Function to restore authentication state - can be called manually
    const restoreAuth = async (): Promise<boolean> => {
        try {
            console.log("Manually restoring auth state...");
            // Create auth client with persistence enabled (default behavior)
            // The AuthClient automatically persists identity in localStorage
            const authClient = await AuthClient.create();

            // Check if user is authenticated
            const isAuthenticated = await authClient.isAuthenticated();

            if (!isAuthenticated) {
                console.log("Auth restore: User is not authenticated");
                setIsLoggedIn(false);
                setIdentity(null);
                setUserPrincipal(null);
                setIsAuthReady(true);
                return false;
            }

            console.log("Auth restore: User is authenticated");
            const retrievedIdentity = authClient.getIdentity();
            const userPrincipalId = retrievedIdentity.getPrincipal();

            // Update state with authenticated user info
            setIdentity(retrievedIdentity);
            setUserPrincipal(userPrincipalId);
            setIsLoggedIn(true);

            // Get platform canister ID
            try {
                const platformCanisterId = await getPlatformCanisterId();

                // Create platform actor
                const platformActor = createAuthenticatedActor(platformCanisterId, retrievedIdentity);

                // Initialize system canisters state
                setSystemCanisters(prev => ({
                    ...prev,
                    platformPrincipal: Principal.fromText(platformCanisterId),
                    platformActor,
                }));

                // Check admin status if in whitelist mode
                try {
                    await checkAdminStatus();
                    console.log('[App] Admin status updated');
                } catch (err) {
                    console.warn('[App] Could not check admin status:', err);
                }

                // Try to get user platform canister
                const existingCanister = await platformActor.getUserPlatformCanister(userPrincipalId);

                if (existingCanister && existingCanister.length > 0) {
                    const userCanisterId = existingCanister[0];
                    console.log('Found existing user platform canister on manual restore:', userCanisterId.toText());

                    // Create user actor
                    const userActor = createAuthenticatedUserActor(userCanisterId.toText(), retrievedIdentity);

                    // Update system canisters with user canister info
                    setSystemCanisters(prev => ({
                        ...prev,
                        userPrincipal: userCanisterId,
                        userActor: userActor
                    }));
                }
            } catch (err) {
                console.error('Error initializing platform actor during manual restore:', err);
                // We still return true since we have the identity, even if we failed to get canisters
            }

            setIsAuthReady(true);
            return true;
        } catch (err) {
            console.error('Error in manual auth restore:', err);
            setIsAuthReady(true); // Still set to true even on failure
            return false;
        }
    };

    // Initialize authentication on component mount
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                console.log("Initializing auth on mount...");
                await restoreAuth();
            } catch (err) {
                console.error('Error during initial auth initialization:', err);
                setIsAuthReady(true);
            }
        };

        initializeAuth();
    }, []);


    useEffect(() => {
        if (isLoggedIn && systemCanisters.platformActor) {
            console.log("Platform actor ready, checking admin status");
            setTimeout(() => {
                checkAdminStatus().then(result =>
                    console.log("Admin check completed with result:", result)
                );
            }, 1000);
        }
    }, [isLoggedIn, systemCanisters.platformActor])


    // Check for redirect after login
    useEffect(() => {
        if (isLoggedIn && isAuthReady) {
            const redirectPath = sessionStorage.getItem('redirectAfterLogin');
            if (redirectPath) {
                console.log("Found redirect path after login:", redirectPath);
                sessionStorage.removeItem('redirectAfterLogin');
                // Use window.location instead of navigate to ensure full page reload
                window.location.href = redirectPath;
            }
        }
    }, [isLoggedIn, isAuthReady]);

    // Values to be provided by the context
    const value: AuthContextType = {
        isLoggedIn,
        setIsLoggedIn,
        userPrincipal,
        setUserPrincipal,
        identity,
        setIdentity,
        systemCanisters,
        setSystemCanisters,
        error,
        setError,
        activeCanister,
        setActiveCanister,
        isAuthReady,
        restoreAuth,
        userPlatformCanister,
        setUserPlatformCanister,
        isAdmin,
        setIsAdmin,
        checkAdminStatus,
        selectedProjectId,
        setSelectedProjectId,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};